/**
 * The iframe session (tss-api #6, D2/D3/D8).
 *
 * Sign-in through the iframe gives an `iframe` session that only the iframe holds: it lives in the iframe's
 * own localStorage (storage partitioned per dApp site, no cookies: Safari blocks third-party cookies) and is
 * never sent to the parent. The parent gets a dApp token pair minted from it (`POST /api/auth/dapp-token`).
 * Losing the storage means a new sign-in.
 *
 * A session saved at the end of a sign-in is unconfirmed until the user passes the consent step (AUTHENTICATE in
 * this iframe). Until then it serves only that step: no dApp pair for a page reload, no session for the message
 * handlers, and it is dropped after UNCONFIRMED_TTL_MS. So a tab closed at the consent step does not sign the user
 * in to the dApp on the next visit.
 */

import { SessionExpiredError } from '../../lib/errors'
import { sdkVersionHeaders } from './sdk-version'

declare const __EMBARK_TSS_URL__: string

export const IFRAME_SESSION_STORAGE_KEY = 'embark-iframe-session'

/** Refresh this long before the access token expires */
const REFRESH_MARGIN_MS = 60 * 1000

/** An unconfirmed session (consent not given) older than this is revoked and forgotten */
export const UNCONFIRMED_TTL_MS = 10 * 60 * 1000

export interface StoredIframeSession {
  accessToken: string
  refreshToken: string
  userId: string
  /** ms epoch */
  expiresAt: number
  /** project the session was signed in through (its `azp`) */
  projectId?: string
  displayName?: string | null
  avatar?: string | null
  providers?: string[]
  hasKeyshare?: boolean
  /** false until the user passes the consent step; absent (sessions stored before) counts as confirmed */
  confirmed?: boolean
  /** ms epoch of the sign-in */
  savedAt?: number
}

/** What a TSS sign-in or refresh answers */
export interface SessionTokens {
  accessToken: string
  refreshToken?: string
  userId: string
  expiresIn: number
  displayName?: string | null
  avatar?: string | null
  providers?: string[]
  hasKeyshare?: boolean
}

/** dApp token pair for the parent (`session_kind: "dapp"`) */
export interface DappTokenPair {
  accessToken: string
  refreshToken: string
  expiresIn: number
  userId: string
}

export interface PublicSessionInfo {
  signedIn: boolean
  userId?: string
  displayName?: string | null
  avatar?: string | null
  providers?: string[]
  hasKeyshare?: boolean
}

type RefreshResult = { token: string; sessionDead?: undefined } | { token: null; sessionDead: boolean }

function defaultTssUrl(): string {
  return (typeof __EMBARK_TSS_URL__ !== 'undefined' && __EMBARK_TSS_URL__) || 'https://api.embarkai.io/tss'
}

export class IframeSession {
  private refreshing: Promise<RefreshResult> | null = null

  constructor(
    private readonly tssUrl: () => string = defaultTssUrl,
    private readonly storage: () => Storage | undefined = () => (typeof localStorage !== 'undefined' ? localStorage : undefined)
  ) {}

  /** The confirmed session (the user passed the consent step), or null */
  get(): StoredIframeSession | null {
    const session = this.getIncludingUnconfirmed()
    return session && session.confirmed !== false ? session : null
  }

  /**
   * The stored session, confirmed or not: for the consent step itself and for the session's own upkeep
   * (refresh, sign-out). A stale unconfirmed session is revoked and forgotten.
   */
  getIncludingUnconfirmed(): StoredIframeSession | null {
    const session = this.read()
    if (session && session.confirmed === false && Date.now() - (session.savedAt ?? 0) > UNCONFIRMED_TTL_MS) {
      void this.discard(session)
      return null
    }
    return session
  }

  has(): boolean {
    return !!this.get()
  }

  getUserId(): string | null {
    return this.get()?.userId ?? null
  }

  /** Whether `token` is (or was, before a refresh) this session's access token */
  owns(token: string | undefined): boolean {
    return !!token && this.read()?.accessToken === token
  }

  /** @param options.confirmed false at the end of a sign-in: the session waits for the consent step */
  save(tokens: SessionTokens, projectId?: string, options: { confirmed?: boolean } = {}): StoredIframeSession {
    if (!tokens.refreshToken) throw new Error('Sign-in response has no refresh token')
    const session: StoredIframeSession = {
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      userId: tokens.userId,
      expiresAt: Date.now() + tokens.expiresIn * 1000,
      projectId,
      displayName: tokens.displayName ?? null,
      avatar: tokens.avatar ?? null,
      providers: tokens.providers ?? [],
      hasKeyshare: tokens.hasKeyshare,
      confirmed: options.confirmed !== false,
      savedAt: Date.now()
    }
    this.write(session)
    return session
  }

  /**
   * The user passed the consent step for `userId`: the session becomes usable.
   * @returns whether a session of that user was confirmed
   */
  confirm(userId: string | undefined): boolean {
    const session = this.getIncludingUnconfirmed()
    if (!session || !userId || session.userId !== userId) return false
    if (session.confirmed === false) this.write({ ...session, confirmed: true })
    return true
  }

  update(patch: Partial<Pick<StoredIframeSession, 'displayName' | 'avatar' | 'providers' | 'hasKeyshare'>>): void {
    const session = this.read()
    if (session) this.write({ ...session, ...patch })
  }

  clear(): void {
    try {
      this.storage()?.removeItem(IFRAME_SESSION_STORAGE_KEY)
    } catch {}
  }

  info(): PublicSessionInfo {
    const session = this.get()
    if (!session) return { signedIn: false }
    return {
      signedIn: true,
      userId: session.userId,
      displayName: session.displayName ?? null,
      avatar: session.avatar ?? null,
      providers: session.providers ?? [],
      hasKeyshare: session.hasKeyshare
    }
  }

  /**
   * A usable access token: refreshed first when it is about to expire.
   * @throws SessionExpiredError when there is no session or it cannot be refreshed any more
   */
  async getAccessToken(): Promise<string> {
    const session = this.getIncludingUnconfirmed()
    if (!session) throw new SessionExpiredError('No iframe session, please sign in')
    if (session.expiresAt - REFRESH_MARGIN_MS > Date.now()) return session.accessToken
    const result = await this.refresh()
    if (result.token) return result.token
    if (result.sessionDead) throw new SessionExpiredError()
    // transient failure: the old token may still be accepted for a few seconds
    return session.accessToken
  }

  /** Refresh with the session's refresh token (rotation). A 401 ends the session. */
  refresh(): Promise<RefreshResult> {
    if (this.refreshing) return this.refreshing
    this.refreshing = this.doRefresh().finally(() => {
      this.refreshing = null
    })
    return this.refreshing
  }

  private async doRefresh(): Promise<RefreshResult> {
    const session = this.getIncludingUnconfirmed()
    if (!session) return { token: null, sessionDead: true }

    let response: Response
    try {
      response = await fetch(this.url('/api/auth/refresh', session.projectId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...sdkVersionHeaders() },
        body: JSON.stringify({ refreshToken: session.refreshToken })
      })
    } catch {
      return { token: null, sessionDead: false }
    }

    if (response.status === 401) {
      // only a JSON answer from TSS ends the session; a proxy 401 is infrastructure
      const body = await response.json().catch(() => null)
      if (body) {
        this.clear()
        return { token: null, sessionDead: true }
      }
      return { token: null, sessionDead: false }
    }
    if (!response.ok) return { token: null, sessionDead: false }

    const data = (await response.json()) as SessionTokens
    // the session may have been replaced (sign-out, another sign-in) while the request was in flight
    const current = this.read()
    if (!current || current.refreshToken !== session.refreshToken) {
      return current ? { token: current.accessToken } : { token: null, sessionDead: true }
    }
    this.write({
      ...current,
      accessToken: data.accessToken,
      refreshToken: data.refreshToken || current.refreshToken,
      expiresAt: Date.now() + data.expiresIn * 1000,
      providers: data.providers ?? current.providers
    })
    return { token: data.accessToken }
  }

  /** Mint a dApp pair for the parent page of `projectId` */
  async mintDappTokens(projectId: string): Promise<DappTokenPair> {
    const call = (token: string) =>
      fetch(this.url('/api/auth/dapp-token', projectId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...sdkVersionHeaders() },
        body: JSON.stringify({ projectId })
      })

    let response = await call(await this.getAccessToken())
    if (response.status === 401) {
      const refreshed = await this.refresh()
      if (!refreshed.token) {
        if (refreshed.sessionDead) throw new SessionExpiredError()
        throw new Error('Token refresh temporarily unavailable, please retry')
      }
      response = await call(refreshed.token)
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw Object.assign(new Error(body?.message || `dApp token request failed (${response.status})`), {
        status: response.status,
        code: body?.error
      })
    }
    return (await response.json()) as DappTokenPair
  }

  /** Revoke the session at TSS (the dApp pair shares its sessionId, so it ends too) and forget it */
  async signOut(): Promise<void> {
    const session = this.read()
    if (!session) return
    // an expired access token would not be accepted by logout: refresh it first when needed
    const token = await this.getAccessToken().catch(() => session.accessToken)
    this.clear()
    try {
      await fetch(this.url('/api/auth/logout', session.projectId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...sdkVersionHeaders() }
      })
    } catch (error) {
      console.warn('[iframe][Session] Logout request failed:', error)
    }
  }

  /** A stale unconfirmed session: forget it and revoke it at TSS (best effort) */
  private async discard(session: StoredIframeSession): Promise<void> {
    if (this.read()?.refreshToken !== session.refreshToken) return
    this.clear()
    try {
      await fetch(this.url('/api/auth/logout', session.projectId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.accessToken}`, ...sdkVersionHeaders() }
      })
    } catch {}
  }

  private read(): StoredIframeSession | null {
    try {
      const raw = this.storage()?.getItem(IFRAME_SESSION_STORAGE_KEY)
      if (!raw) return null
      const parsed = JSON.parse(raw) as StoredIframeSession
      return parsed?.accessToken && parsed?.refreshToken && parsed?.userId ? parsed : null
    } catch {
      return null
    }
  }

  private url(path: string, projectId?: string): string {
    const url = `${this.tssUrl()}${path}`
    return projectId ? `${url}${url.includes('?') ? '&' : '?'}projectId=${encodeURIComponent(projectId)}` : url
  }

  private write(session: StoredIframeSession): void {
    this.storage()?.setItem(IFRAME_SESSION_STORAGE_KEY, JSON.stringify(session))
  }
}

/** The iframe's session, shared by the message handlers and the API clients */
export const iframeSession = new IframeSession()
